import React, { useState, useEffect, FC } from "react";
import { useNavigate } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { incorporadoraService } from "../../services/incorporadoraService";
import {
  ufService,
  municipioService,
  bairroService,
} from "../../services/enderecoService";
import { EmpreendimentoCreate } from "../../types/empreendimento";
import { Incorporadora } from "../../types/incorporadora";
import { Option, UF } from "../../types/localidade"; // Importar UF e Option
import { IncorporadoraModal } from "./componentes/IncorporadoraModal";
import { levenshteinDistance } from "../../utils/levenshtein";

// --- Interfaces ---

interface EnderecoFormData {
  logradouro: {
    cidade: number | ""; // ID do município
    bairro: number | ""; // ID do bairro
    zipcode: string; // CEP
    nome: string;
  };
  numero?: number | "";
  complemento: string;
  ponto_referencia: string;
  latitude: string;
  longitude: string;
}

interface FormData {
  nome: string;
  incorporadora: number | "";
  data_entrega: string;
  data_inicio_comercializacao: string;
  observacao: string;
  area_total: number | "";
  area_construida: number | "";
  is_lancamento: boolean;
  endereco: EnderecoFormData;
  elevadores: number | "";
  vagas_rotativas: number | "";
  vagas: number | "";
}

interface EmpreendimentoFormBasicoProps {
  onNext: (data: EmpreendimentoCreate) => void;
  initialData?: Partial<EmpreendimentoCreate>;
  /**
   * Texto livre de localização (ex.: vindo da importação por IA) pra tentar
   * casar automaticamente com UF/município/bairro já cadastrados — mesmo
   * mecanismo (Levenshtein) já usado no autofill de CEP, só que disparado
   * pelo texto em vez de uma resposta do ViaCEP.
   */
  initialEnderecoTexto?: { uf?: string; cidade?: string; bairro?: string };
  /** Nome de incorporadora pra tentar casar automaticamente por Levenshtein. */
  initialIncorporadoraTexto?: string;
  /** Reporta os dados atuais e se já passam nas validações obrigatórias a
   * cada mudança — não só quando "Próximo" é clicado. O wizard pai usa isso
   * pra manter os dados básicos sempre disponíveis (mesmo sem confirmar essa
   * etapa) e pra colorir o círculo dela na barra de progresso. */
  onDataChange?: (data: EmpreendimentoCreate, isValid: boolean) => void;
}

const EmpreendimentoFormBasico: FC<EmpreendimentoFormBasicoProps> = ({
  onNext,
  initialData,
  initialEnderecoTexto,
  initialIncorporadoraTexto,
  onDataChange,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [incorporadoras, setIncorporadoras] = useState<Incorporadora[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // --- Estados para Endereço ---
  const [ufs, setUfs] = useState<UF[]>([]);
  const [municipios, setMunicipios] = useState<Option[]>([]);
  const [bairros, setBairros] = useState<Option[]>([]);

  const [selectedUfId, setSelectedUfId] = useState<number | null>(null);
  const [selectedMunicipioId, setSelectedMunicipioId] = useState<number | null>(
    null
  );

  const [loadingUfs, setLoadingUfs] = useState(true);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [loadingBairros, setLoadingBairros] = useState(false);

  const [isIncorporadoraModalOpen, setIsIncorporadoraModalOpen] =
    useState(false);

  const [formData, setFormData] = useState<FormData>({
    nome: "",
    vagas: "",
    vagas_rotativas: "",
    incorporadora: "",
    data_entrega: "",
    data_inicio_comercializacao: "",
    observacao: "",
    area_total: "",
    area_construida: "",
    is_lancamento: false,
    elevadores: "",
    endereco: {
      logradouro: {
        cidade: "",
        bairro: "",
        zipcode: "",
        nome: "",
      },
      numero: undefined,
      complemento: "",
      ponto_referencia: "",
      latitude: "",
      longitude: "",
    },
  });

  // --- Efeitos ---

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingUfs(true);
      await fetchIncorporadoras();
      const ufsResponse = await ufService.listar();
      setUfs(ufsResponse || []);
      setLoadingUfs(false);
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (initialData && ufs.length > 0) {
      const { endereco, ...rest } = initialData;
      const newFormData: FormData = {
        ...formData,
        nome: rest.nome || "",
        vagas: rest.vagas || 0,
        vagas_rotativas: rest.vagas_rotativas || 0,
        incorporadora: rest.incorporadora || 0,
        data_entrega: rest.data_entrega?.split("T")[0] || "",
        data_inicio_comercializacao:
          rest.data_inicio_comercializacao?.split("T")[0] || "",
        observacao: rest.observacao || "",
        area_total: rest.area_total || 0,
        area_construida: rest.area_construida || 0,
        is_lancamento: rest.is_lancamento || false,
        elevadores: rest.elevadores || 0,
        endereco: { ...formData.endereco },
      };

      if (
        endereco &&
        typeof endereco === "object" &&
        endereco.logradouro &&
        typeof endereco.logradouro === "object"
      ) {
        const { logradouro, ...restEndereco } = endereco;
        newFormData.endereco = {
          ...newFormData.endereco,
          ...restEndereco,
          logradouro: {
            ...newFormData.endereco.logradouro, // Manter valores padrão
            zipcode: (logradouro as any).zipcode,
            nome: (logradouro as any).nome,
            cidade:
              typeof (logradouro as any).cidade === "object" &&
              (logradouro as any).cidade
                ? (logradouro as any).cidade.id
                : (logradouro as any).cidade || 0,
            bairro:
              typeof (logradouro as any).bairro === "object" &&
              (logradouro as any).bairro // Corrigido para usar o nome do bairro
                ? (logradouro as any).bairro.nome
                : String((logradouro as any).bairro || ""), // Garante que seja sempre string
          },
        };

        if (
          typeof logradouro.cidade === "object" &&
          logradouro.cidade &&
          (logradouro.cidade as any).uf
        ) {
          setSelectedUfId((logradouro.cidade as any).uf.id);
          setSelectedMunicipioId((logradouro.cidade as any).id); // Armazena o ID
        } else if (typeof logradouro.cidade === "number") {
          setSelectedMunicipioId(logradouro.cidade); // Se já é um ID, armazena
        }
      }
      setFormData(newFormData);
    }
  }, [initialData, ufs]);

  useEffect(() => {
    if (!selectedUfId) {
      setMunicipios([]); // Limpa municípios
      setSelectedMunicipioId(null); // Limpa o ID do município selecionado
      return;
    }
    const fetchMunicipios = async () => {
      setLoadingMunicipios(true);
      const response = await municipioService.listarPorUF(selectedUfId);
      setMunicipios(response || []);
      setLoadingMunicipios(false);
    };
    fetchMunicipios();
  }, [selectedUfId]);

  useEffect(() => {
    if (!selectedMunicipioId) {
      // Agora depende do ID do município
      setBairros([]);
      handleInputChange("endereco.logradouro.bairro", ""); // Limpa o nome do bairro
      return;
    }
    const fetchBairros = async () => {
      setLoadingBairros(true);
      const response = await bairroService.listarPorMunicipio(
        // Passa o ID
        selectedMunicipioId
      );
      setBairros(response || []);
      setLoadingBairros(false);
    };
    fetchBairros();
  }, [selectedMunicipioId]);

  const fetchIncorporadoras = async () => {
    try {
      const data = await incorporadoraService.listarParaSelecao();
      setIncorporadoras(data);
    } catch (error) {
      console.error("Erro ao buscar incorporadoras:", error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    const keys = field.split(".");
    setFormData((prev) => {
      const updated: any = { ...prev };
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]] = { ...current[keys[i]] };
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  /**
   * Casa UF (por sigla exata) + município + bairro (por Levenshtein) contra o
   * que já está cadastrado, e aplica no formData. Compartilhado pelo autofill
   * de CEP (ViaCEP) e pelo pré-preenchimento vindo da importação por IA — os
   * dois só diferem na origem do texto, a resolução é a mesma.
   */
  const resolverEnderecoPorTexto = async (
    ufSigla?: string,
    cidadeTexto?: string,
    bairroTexto?: string
  ) => {
    if (!ufSigla) return;
    const ufMatch = ufs.find((uf) => uf.sigla.toUpperCase() === ufSigla.toUpperCase());
    if (!ufMatch) return;

    setSelectedUfId(ufMatch.id);
    if (!cidadeTexto) return;

    const municipiosUF = await municipioService.listarPorUF(ufMatch.id);
    const municipioMatch = municipiosUF
      .map((m) => ({ m, distance: levenshteinDistance(cidadeTexto, m.label) }))
      .sort((a, b) => a.distance - b.distance)[0];
    if (!municipioMatch) return;

    setSelectedMunicipioId(Number(municipioMatch.m.value));
    handleInputChange("endereco.logradouro.cidade", Number(municipioMatch.m.value));
    if (!bairroTexto) return;

    const bairrosMun = await bairroService.listarPorMunicipio(Number(municipioMatch.m.value));
    const bairroMatch = bairrosMun
      .map((b) => ({ b, distance: levenshteinDistance(bairroTexto, b.label) }))
      .sort((a, b) => a.distance - b.distance)[0];
    if (bairroMatch) {
      handleInputChange("endereco.logradouro.bairro", Number(bairroMatch.b.value));
    }
  };

  const handleCepSearch = async (cep: string) => {
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) {
        alert("CEP não encontrado");
        return;
      }

      handleInputChange("endereco.logradouro.nome", data.logradouro || "");
      handleInputChange("endereco.complemento", data.complemento || "");
      handleInputChange("endereco.logradouro.zipcode", cep);

      await resolverEnderecoPorTexto(data.uf, data.localidade, data.bairro);
    } catch (err) {
      console.error("Erro ao buscar CEP:", err);
    }
  };

  // Pré-preenchimento vindo da importação por IA: só dispara uma vez, quando
  // as UFs já carregaram (resolverEnderecoPorTexto precisa da lista de UFs).
  const [enderecoIaResolvido, setEnderecoIaResolvido] = useState(false);
  useEffect(() => {
    if (enderecoIaResolvido || !initialEnderecoTexto || ufs.length === 0) return;
    setEnderecoIaResolvido(true);
    resolverEnderecoPorTexto(
      initialEnderecoTexto.uf,
      initialEnderecoTexto.cidade,
      initialEnderecoTexto.bairro
    );
  }, [initialEnderecoTexto, ufs, enderecoIaResolvido]);

  // Idem para incorporadora: casa por Levenshtein contra razão social/nome
  // fantasia já cadastrados. Só aplica se achar algo razoavelmente parecido —
  // senão fica em branco pra revisão manual em vez de escolher qualquer coisa.
  const [incorporadoraIaResolvida, setIncorporadoraIaResolvida] = useState(false);
  useEffect(() => {
    if (incorporadoraIaResolvida || !initialIncorporadoraTexto || incorporadoras.length === 0) {
      return;
    }
    setIncorporadoraIaResolvida(true);
    const melhor = incorporadoras
      .map((inc) => ({
        inc,
        distance: Math.min(
          levenshteinDistance(initialIncorporadoraTexto, inc.razao_social || ""),
          levenshteinDistance(initialIncorporadoraTexto, inc.nome_fantasia || "")
        ),
      }))
      .sort((a, b) => a.distance - b.distance)[0];
    // Tolerância proporcional ao tamanho do texto: evita "casar" com algo
    // completamente diferente só porque é o "menos pior" da lista.
    if (melhor && melhor.distance <= initialIncorporadoraTexto.length * 0.4) {
      handleInputChange("incorporadora", melhor.inc.id);
    }
  }, [initialIncorporadoraTexto, incorporadoras, incorporadoraIaResolvida]);

  // Extraídas de handleSubmit pra serem reutilizadas também pelo reporte
  // contínuo de validade/dados (useEffect logo abaixo) — a mesma regra que
  // bloqueia o clique em "Próximo" agora também decide a cor do círculo
  // dessa etapa na barra de progresso do wizard.
  const getValidationErrors = (
    fd: FormData,
    ufId: number | null,
    munId: number | null
  ): { [key: string]: string } => {
    const newErrors: { [key: string]: string } = {};

    if (!fd.nome.trim())
      newErrors.nome = "Nome do empreendimento é obrigatório";
    if (!fd.incorporadora)
      newErrors.incorporadora = "Incorporadora é obrigatória";
    if (!ufId) newErrors.uf = "UF é obrigatório";
    if (!munId) newErrors.municipio = "Município é obrigatório";
    if (fd.endereco.logradouro.zipcode.length !== 8)
      newErrors.zipcode = "CEP deve ter 8 dígitos";
    if (!fd.endereco.logradouro.nome.trim())
      newErrors.logradouro = "Logradouro é obrigatório";

    if (!fd.endereco.latitude) {
      newErrors.latitude = "Latitude é obrigatória";
    } else if (!/^-?\d{1,3}\.\d+$/.test(fd.endereco.latitude)) {
      newErrors.latitude = "Latitude inválida.";
    } else if (
      fd.endereco.latitude.length < 15 ||
      fd.endereco.latitude.length > 25
    ) {
      newErrors.latitude = "Latitude deve ter entre 15 e 25 caracteres.";
    }

    if (!fd.endereco.longitude) {
      newErrors.longitude = "Longitude é obrigatória";
    } else if (!/^-?\d{1,3}\.\d+$/.test(fd.endereco.longitude)) {
      newErrors.longitude = "Longitude inválida.";
    } else if (
      fd.endereco.longitude.length < 15 ||
      fd.endereco.longitude.length > 25
    ) {
      newErrors.longitude = "Longitude deve ter entre 15 e 25 caracteres.";
    }

    return newErrors;
  };

  const buildPayload = (fd: FormData) => ({
    ...fd,
    area_total: Number(fd.area_total) || 0,
    area_construida: Number(fd.area_construida) || 0,
    endereco: {
      ...fd.endereco,
      numero: fd.endereco.numero ? Number(fd.endereco.numero) : undefined,
      latitude: fd.endereco.latitude || null,
      longitude: fd.endereco.longitude || null,
      logradouro: {
        ...fd.endereco.logradouro,
        cidade: fd.endereco.logradouro.cidade,
        bairro: fd.endereco.logradouro.bairro,
      },
    },
    tabela_preco: JSON.stringify({}),
  });

  // Reporte contínuo pro pai — roda a cada digitação, não só no submit.
  useEffect(() => {
    const isValid =
      Object.keys(getValidationErrors(formData, selectedUfId, selectedMunicipioId))
        .length === 0;
    onDataChange?.(buildPayload(formData) as EmpreendimentoCreate, isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, selectedUfId, selectedMunicipioId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = getValidationErrors(formData, selectedUfId, selectedMunicipioId);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    try {
      const empreendimentoData = buildPayload(formData);
      onNext(empreendimentoData as EmpreendimentoCreate);
    } catch (error) {
      console.error("Erro ao processar dados:", error);
      alert("Erro ao processar dados do empreendimento. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleIncorporadoraCreated = async (newIncorporadoraId: number) => {
    await fetchIncorporadoras();
    handleInputChange("incorporadora", newIncorporadoraId);
  };


  const ufOptions: Option[] = ufs.map(uf => ({ value: String(uf.id), label: uf.nome }));
  const currentUfOption: SingleValue<Option> =
    ufOptions.find((opt) => Number(opt.value) === selectedUfId) || null; // value é ID (number)
  const currentMunicipioOption: SingleValue<Option> =
    municipios.find((opt) => Number(opt.value) === selectedMunicipioId) || null; // value é ID (number)
  const currentBairroOption =
    bairros.find(
      (opt) => opt.value === String(formData.endereco.logradouro.bairro)
    ) || null;

  return (
    <div className="max-w-4xl mx-auto px-6">
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            Informações Básicas
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Incorporadora *
              </label>
              <div className="flex items-end gap-2">
                <div className="flex-grow">
                  <Select
                    inputId="incorporadora"
                    options={incorporadoras.map((inc) => ({
                      value: inc.id,
                      label: inc.razao_social || `ID ${inc.id}`,
                    }))}
                    value={
                      formData.incorporadora
                        ? {
                            value: formData.incorporadora,
                            label:
                              incorporadoras.find((inc) => inc.id === formData.incorporadora)
                                ?.razao_social || `ID ${formData.incorporadora}`,
                          }
                        : null
                    }
                    onChange={(opt: SingleValue<{ value: number; label: string }>) =>
                      handleInputChange("incorporadora", opt ? opt.value : "")
                    }
                    placeholder="Selecione ou busque..."
                    isClearable
                    noOptionsMessage={() => "Nenhuma incorporadora encontrada"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setIsIncorporadoraModalOpen(true)}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                >
                  +
                </button>
              </div>
              {errors.incorporadora && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.incorporadora}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Empreendimento *
              </label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => handleInputChange("nome", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              {errors.nome && (
                <p className="text-red-500 text-xs mt-1">{errors.nome}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Área do Terreno (m²)
              </label>
              <input
                type="number"
                value={formData.area_total}
                onChange={(e) =>
                  handleInputChange(
                    "area_total",
                    parseFloat(e.target.value) || 0
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Área Construída (m²)
              </label>
              <input
                type="number"
                value={formData.area_construida}
                onChange={(e) =>
                  handleInputChange(
                    "area_construida",
                    parseFloat(e.target.value) || 0
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Elevadores
              </label>
              <input
                type="number"
                min={0}
                value={formData.elevadores}
                onChange={(e) =>
                  handleInputChange("elevadores", parseInt(e.target.value) || 0)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Início da Comercialização
              </label>
              <input
                type="date"
                value={formData.data_inicio_comercializacao}
                onChange={(e) =>
                  handleInputChange(
                    "data_inicio_comercializacao",
                    e.target.value
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Entrega
              </label>
              <input
                type="date"
                value={formData.data_entrega}
                onChange={(e) =>
                  handleInputChange("data_entrega", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vagas totais
              </label>
              <input
                type="number"
                min={0}
                value={formData.vagas}
                onChange={(e) =>
                  handleInputChange("vagas", parseInt(e.target.value) || 0)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vagas Rotativas
              </label>
              <input
                type="number"
                min={0}
                value={formData.vagas_rotativas}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  // se apagar o campo
                  if (rawValue === "") {
                    handleInputChange("vagas_rotativas", 0);
                    return;
                  }
                  let v = Number(rawValue);
                  const vagasMax =
                    typeof formData.vagas === "number" ? formData.vagas : 0;
                  if (v > vagasMax) v = vagasMax;
                  handleInputChange("vagas_rotativas", v);
                }}

                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                value={formData.observacao}
                onChange={(e) =>
                  handleInputChange("observacao", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
              />
            </div>
            <div className="flex items-center">
              <input
                id="is_lancamento"
                type="checkbox"
                name="is_lancamento"
                checked={formData.is_lancamento}
                onChange={(e) =>
                  handleInputChange("is_lancamento", e.target.checked)
                }
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label
                htmlFor="is_lancamento"
                className="ml-2 block text-sm font-medium text-gray-700"
              >
                É Lançamento?
              </label>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CEP *
              </label>
              <input
                type="text"
                value={formData.endereco.logradouro.zipcode}
                onChange={(e) => {
                  const cep = e.target.value.replace(/\D/g, "");
                  handleInputChange("endereco.logradouro.zipcode", cep);
                  if (cep.length === 8) handleCepSearch(cep);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                maxLength={8}
              />
              {errors.zipcode && (
                <p className="text-red-500 text-xs mt-1">{errors.zipcode}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                UF *
              </label>
              <Select
                options={ufOptions}
                value={currentUfOption}
                onChange={(opt) => {
                  setSelectedUfId(opt ? Number(opt.value) : null); // Armazena o ID
                  const selectedUfObject = ufs.find(uf => uf.id === Number(opt?.value));
                  handleInputChange("endereco.uf_sigla", selectedUfObject ? selectedUfObject.sigla : "");
                }}
                isDisabled={loadingUfs}
                isLoading={loadingUfs}
                placeholder="Selecione"
                isClearable
              />
              {errors.uf && (
                <p className="text-red-500 text-xs mt-1">{errors.uf}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Município *
              </label>
              <Select // value é ID (number)
                options={municipios}
                value={currentMunicipioOption}
                onChange={(opt: SingleValue<Option>) => {
                  setSelectedMunicipioId(opt ? Number(opt.value) : null); // Armazena o ID
                  handleInputChange(
                    "endereco.logradouro.cidade",
                    opt ? Number(opt.value) : 0 // Passa o ID para o formData
                  );
                }}
                isDisabled={!selectedUfId || loadingMunicipios}
                isLoading={loadingMunicipios}
                placeholder="Selecione"
                isClearable
              />
              {errors.municipio && (
                <p className="text-red-500 text-xs mt-1">{errors.municipio}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bairro
              </label>
              <Select // value é nome (string)
                options={bairros}
                value={currentBairroOption}
                onChange={(opt: SingleValue<Option>) =>
                  handleInputChange(
                    // Passa o nome do bairro
                    "endereco.logradouro.bairro",
                    opt ? Number(opt.value) : 0 // Passa o ID do bairro
                  )
                }
                isDisabled={!selectedMunicipioId || loadingBairros}
                isLoading={loadingBairros}
                placeholder="Selecione"
                isClearable
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logradouro *
              </label>
              <input
                type="text"
                value={formData.endereco.logradouro.nome}
                onChange={(e) =>
                  handleInputChange("endereco.logradouro.nome", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              {errors.logradouro && (
                <p className="text-red-500 text-xs mt-1">{errors.logradouro}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número
              </label>
              <input
                type="number"
                value={formData.endereco.numero || ""}
                onChange={(e) =>
                  handleInputChange(
                    "endereco.numero",
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Complemento
              </label>
              <input
                type="text"
                value={formData.endereco.complemento}
                onChange={(e) =>
                  handleInputChange("endereco.complemento", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude
              </label>
              <input
                type="text"
                value={formData.endereco.latitude}
                onChange={(e) =>
                  handleInputChange(
                    "endereco.latitude",
                    e.target.value.replace(/[^0-9\.-]/g, "").slice(0, 25)
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              {errors.latitude && (
                <p className="text-red-500 text-xs mt-1">{errors.latitude}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude
              </label>
              <input
                type="text"
                value={formData.endereco.longitude}
                onChange={(e) =>
                  handleInputChange(
                    "endereco.longitude",
                    e.target.value.replace(/[^0-9\.-]/g, "").slice(0, 25)
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              {errors.longitude && (
                <p className="text-red-500 text-xs mt-1">{errors.longitude}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/empreendimentos")}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Processando..." : "Próximo"}
          </button>
        </div>
      </form>
      <IncorporadoraModal
        isOpen={isIncorporadoraModalOpen}
        onClose={() => setIsIncorporadoraModalOpen(false)}
        onSuccess={handleIncorporadoraCreated}
      />
    </div>
  );
};

export default EmpreendimentoFormBasico;
